import { Plus, Search, Users } from 'lucide-react'
import { useDeferredValue, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { PageHeader } from '@/components/common/PageHeader'
import { Pager } from '@/components/common/Pager'
import { usePagination } from '@/components/common/usePagination'
import { EmptyState, ErrorState } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ConfirmModal } from '@/components/ui/modal'
import { useAuth } from '@/lib/auth/AuthProvider'
import { useDirectoryUsers, useResetUserPassword, useSetUserActive } from './api'
import { UserFormDialog } from './components/UserFormDialog'
import { UsersTable } from './components/UsersTable'
import { USER_WRITE_PERM } from './permissions'
import type { DirectoryUserRow } from './types'
import { toastError } from './ui'

export function UsersPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  const setActive = useSetUserActive()
  const resetPassword = useResetUserPassword()
  const [q, setQ] = useState('')
  const search = useDeferredValue(q.trim().toLocaleLowerCase('tr'))
  const [dialog, setDialog] = useState<{ open: boolean; user: DirectoryUserRow | null }>({ open: false, user: null })
  const [pendingToggle, setPendingToggle] = useState<DirectoryUserRow | null>(null)
  const [pendingReset, setPendingReset] = useState<DirectoryUserRow | null>(null)
  const canWrite = can(USER_WRITE_PERM)
  const users = useDirectoryUsers()
  const all = users.data
  const filtered = useMemo(
    () =>
      (all ?? []).filter(
        (u) => !search || `${u.name} ${u.email} ${u.departmentName} ${u.position} ${u.roleNames.join(' ')}`.toLocaleLowerCase('tr').includes(search),
      ),
    [all, search],
  )
  const { pageRows: rows, pager } = usePagination(filtered, search)
  const total = all?.length ?? 0

  const confirmToggle = async () => {
    if (!pendingToggle) return
    try {
      await setActive.mutateAsync({ id: pendingToggle.id, active: !pendingToggle.isActive })
      toast.success(t(pendingToggle.isActive ? 'access.directory.deactivated' : 'access.directory.activated'))
      setPendingToggle(null)
    } catch (e) {
      setPendingToggle(null)
      toastError(e)
    }
  }

  const confirmReset = async () => {
    if (!pendingReset) return
    try {
      await resetPassword.mutateAsync(pendingReset.id)
      toast.success(t('access.directory.passwordReset'))
      setPendingReset(null)
    } catch (e) {
      setPendingReset(null)
      toastError(e)
    }
  }

  return (
    <>
      <PageHeader
        eyebrow={t('nav.groups.access')}
        title={t('access.directory.title')}
        description={t('access.directory.subtitle')}
        actions={
          canWrite ? (
            <Button onClick={() => setDialog({ open: true, user: null })}>
              <Plus />
              {t('access.directory.create')}
            </Button>
          ) : null
        }
      />
      <section className="overflow-hidden rounded-2xl border bg-card">
        <div className="flex flex-wrap items-center gap-2 border-b p-3">
          <div className="relative w-full max-w-xs">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('access.directory.searchPlaceholder')} aria-label={t('access.directory.searchPlaceholder')} className="pl-8" />
          </div>
          <span className="ml-auto text-xs text-muted-foreground">{t('access.directory.count', { count: filtered.length, total })}</span>
        </div>
        {users.isError ? (
          <ErrorState error={users.error} onRetry={() => void users.refetch()} />
        ) : (
          <>
            <UsersTable
              rows={rows}
              loading={users.isLoading}
              canWrite={canWrite}
              onEdit={(u) => setDialog({ open: true, user: u })}
              onToggleActive={setPendingToggle}
              onResetPassword={setPendingReset}
            />
            {!users.isLoading && filtered.length === 0 ? (
              <EmptyState icon={Users} title={total ? t('access.directory.noMatch') : t('access.directory.empty')} />
            ) : null}
            <Pager {...pager} />
          </>
        )}
      </section>

      <UserFormDialog open={dialog.open} user={dialog.user} onOpenChange={(open) => setDialog((s) => ({ ...s, open }))} />
      <ConfirmModal
        open={!!pendingToggle}
        onOpenChange={(v) => !v && setPendingToggle(null)}
        title={t(pendingToggle?.isActive ? 'access.directory.deactivateTitle' : 'access.directory.activateTitle')}
        description={t(pendingToggle?.isActive ? 'access.directory.deactivateBody' : 'access.directory.activateBody', {
          name: pendingToggle?.name ?? '',
        })}
        confirmLabel={t(pendingToggle?.isActive ? 'access.directory.deactivate' : 'access.directory.activate')}
        destructive={!!pendingToggle?.isActive}
        loading={setActive.isPending}
        onConfirm={() => void confirmToggle()}
      />
      <ConfirmModal
        open={!!pendingReset}
        onOpenChange={(v) => !v && setPendingReset(null)}
        title={t('access.directory.resetTitle')}
        description={t('access.directory.resetBody', { name: pendingReset?.name ?? '' })}
        confirmLabel={t('access.directory.resetPassword')}
        loading={resetPassword.isPending}
        onConfirm={() => void confirmReset()}
      />
    </>
  )
}
